import { useCallback, useRef, useState } from 'react';

import { Logger } from '@fhs/cart/src/payment/core';
import type { CardData, EncryptionResult, VaultResult } from '@fhs/cart/src/payment/payment-types';
import { EncryptionService } from '@fhs/cart/src/payment/services/encryption-service';
import { VaultService } from '@fhs/cart/src/payment/services/vault-service';
import { usePaymentMethodStore } from '@fhs/cart/src/state/payment-stores';
import { PaymentProcessor as GatewayPaymentProcessor } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';

export interface VaultNewCardResult {
  accountIdentifier: string;
  success: boolean;
}

export interface UseVaultNewCardOptions {
  storeId: string;
  processor: PaymentProcessor | null | undefined;
}

/**
 * Hook to vault a new credit card following the existing pipeline architecture.
 *
 * This hook encapsulates the encryption and vaulting flow:
 * 1. Creates an EncryptionService to encrypt card data
 * 2. Uses VaultService to vault the encrypted card
 * 3. Returns the accountIdentifier for use in subsequent operations (e.g., gift card reload)
 */
export function useVaultNewCard({ storeId, processor }: UseVaultNewCardOptions) {
  const [isVaulting, setIsVaulting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loggerRef = useRef(new Logger('useVaultNewCard'));
  const encryptionServiceRef = useRef<EncryptionService | null>(null);

  const vaultNewCard = useCallback(
    async (cardData: CardData): Promise<VaultNewCardResult | null> => {
      const logger = loggerRef.current;

      if (!processor) {
        logger.logError('No payment processor available');
        setError('Payment processor not configured');
        return null;
      }

      setIsVaulting(true);
      setError(null);

      try {
        logger.log('Starting new card vaulting flow', {
          processor,
          storeId,
          hasCardData: !!cardData,
        });

        // Step 1: Create encryption service and encrypt card data
        if (!encryptionServiceRef.current) {
          encryptionServiceRef.current = new EncryptionService();
        }
        const encryptionService = encryptionServiceRef.current;

        // Store the encryption service in the payment method store for WebView access
        usePaymentMethodStore.getState().setEncryptionService(encryptionService);

        logger.log('Encrypting card data', { processor });

        // Cast processor to gateway type (both have ORBITAL and WORLDPAY which are the only ones used)
        const encryptionResult: EncryptionResult = await encryptionService.encrypt(
          cardData,
          processor as unknown as GatewayPaymentProcessor
        );

        logger.log('Encryption successful', {
          processor,
          hasEncryptionResult: !!encryptionResult,
        });

        // Step 2: Vault the encrypted card
        const vaultService = new VaultService({
          storeId,
          cardData,
          logger,
        });

        logger.log('Vaulting encrypted card', { processor });

        const vaultResult: VaultResult = await vaultService.vaultCard(
          encryptionResult,
          processor as unknown as GatewayPaymentProcessor
        );

        if (!vaultResult.success || !vaultResult.accountIdentifier) {
          throw new Error(vaultResult.error || 'Vaulting failed - no account identifier returned');
        }

        logger.log('Card vaulted successfully', {
          processor,
          accountIdentifier: vaultResult.accountIdentifier,
        });

        return {
          accountIdentifier: vaultResult.accountIdentifier,
          success: true,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error during vaulting';
        logger.logError('Vaulting failed', err);
        setError(errorMessage);
        return null;
      } finally {
        setIsVaulting(false);
        // Clean up encryption service reference
        usePaymentMethodStore.getState().setEncryptionService(null);
      }
    },
    [storeId, processor]
  );

  return {
    vaultNewCard,
    isVaulting,
    error,
  };
}
